import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Modal,
  Input,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Tr,
  useDisclosure,
  Button,
  useColorModeValue,
  Select,
  Badge,
  IconButton,
} from "@chakra-ui/react";
import React, { useState, useCallback, useMemo } from "react";
import { AddIcon, CopyIcon } from "@chakra-ui/icons";
import { MdQrCode2, MdHistory } from "react-icons/md";
import { useTranslation } from "react-i18next";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { NavLink, useNavigate } from "react-router-dom";

import Card from "components/card/Card";
import CryptoReceiveModal from "components/card/CryptoReceiveModal";
import constants from "providers/Constants";
import useEndpoints from "providers/Endpoints";

const columnHelper = createColumnHelper();

export default function CryptoWalletsTable(props) {
  const { t } = useTranslation();
  const { tableData, sortArray, networks, index, onRetrieveBalance, searchQuery } = props;
  const endpoints = useEndpoints();
  const navigate = useNavigate();

  const [sorting, setSorting] = useState([]);
  const [error, setError] = useState([]);
  const [loading, setLoading] = useState(false);
  const [postData, setPostData] = useState({});
  const [revealedBalances, setRevealedBalances] = useState(new Set());
  const [loadingBalances, setLoadingBalances] = useState(new Set());
  const [{ pageIndex, pageSize }, setPagination] = useState({ pageIndex: 0, pageSize: 5 });
  const [previousWalletCount, setPreviousWalletCount] = useState(tableData.length);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [receiveDetails, setReceiveDetails] = useState(null);
  const [loadingReceiveDetails, setLoadingReceiveDetails] = useState(false);
  const [selectedReceiveWallet, setSelectedReceiveWallet] = useState(null);
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();

  // Filter table data based on search query
  const filteredTableData = useMemo(() => {
    if (!searchQuery || searchQuery.trim() === "") {
      return tableData;
    }
    const query = searchQuery.toLowerCase().trim();
    return tableData.filter((wallet) => {
      const label = wallet.label?.toLowerCase() || "";
      const address = wallet.address?.toLowerCase() || "";
      const currency = wallet.currency?.toLowerCase() || "";
      const network = wallet.network?.toLowerCase() || "";
      
      return label.includes(query) || address.includes(query) || currency.includes(query) || network.includes(query);
    });
  }, [tableData, searchQuery]);

  const handleModalClose = useCallback(() => {
    setPostData({});
    setError([]);
    onModalClose();
  }, [onModalClose]);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  const copyToClipboard = useCallback((address) => {
    if (!address || address === "N/A") {
      constants.showToast("error", t("FAILED"), t("No address to copy"));
      return;
    }
    navigator.clipboard.writeText(address).then(() => {
      constants.showToast("success", t("COPIED"), t("Address copied to clipboard"));
    }).catch(() => {
      constants.showToast("error", t("FAILED"), t("Failed to copy address"));
    });
  }, [t]);

  const typeColorMap = useMemo(() => ({
    main_wallet: { bg: "brand.500", color: "white", label: "main" },
    sub_wallet: { bg: "blue.100", color: "blue.800", label: "sub" },
  }), []);

  const getTypeColor = useCallback((type) => {
    return typeColorMap[type] || { bg: "gray.100", color: "gray.800", label: type };
  }, [typeColorMap]);

  const renderBalance = useCallback((balance, walletNetwork = "", symbol = "", showButton = false, walletAddress = "", isNullBalance = false, shouldBlur = false, isMainWallet = false) => {
    const isRevealed = revealedBalances.has(walletAddress);
    const isLoading = loadingBalances.has(walletAddress);
    const shouldShowBlurred = (showButton && !isRevealed && !isMainWallet) || balance === null || balance === undefined;

    if (shouldShowBlurred) {
      return (
        <Flex align="center" gap={2}>
          <Text color={textColor} fontSize="sm" fontWeight="700" filter="blur(4px)" userSelect="none">
            ••••••••
          </Text>
          {showButton && (
            <Button size="xs" variant="outline" colorScheme="brand" fontSize="10px" height="24px" isLoading={isLoading}
              onClick={() => retrieveBalance(walletNetwork, walletAddress, isNullBalance)}>
              {isLoading ? "" : t("Retrieve")}
            </Button>
          )}
        </Flex>
      );
    }
    return (
      <Flex align="center" gap={2}>
        <Text color={textColor} fontSize="sm" fontWeight="700" filter={shouldBlur ? "blur(4px)" : "none"}>
          {symbol} {Number(balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}
        </Text>
        {showButton && (
          <Button size="xs" variant="outline" colorScheme="brand" fontSize="10px" height="24px" isLoading={isLoading}
            onClick={() => retrieveBalance(walletNetwork, walletAddress, isNullBalance)}>
            {isLoading ? "" : t("Refresh")}
          </Button>
        )}
      </Flex>
    );
  }, [revealedBalances, loadingBalances, textColor, t]);

  const retrieveBalance = useCallback((walletNetwork, walletAddress, isNullBalance = false) => {
    if (onRetrieveBalance) {
      setLoadingBalances(prev => new Set(prev).add(walletAddress));
      onRetrieveBalance(walletNetwork, walletAddress, () => {
        setLoadingBalances(prev => {
          const newSet = new Set(prev);
          newSet.delete(walletAddress);
          return newSet;
        });
      });
    }
    setRevealedBalances(prev => new Set(prev).add(walletAddress));
  }, [onRetrieveBalance]);

  const proceed = useCallback(async () => {
    if (!postData?.network || !postData?.transfer_network) {
      return constants.showToast("error", t("FAILED"), t("Please select a crypto network and transfer network"));
    }
    setLoading(true);
    endpoints.createCryptoWallet(postData, (error) => {
      setLoading(false);
      if (error) {
        setError(error.errors);
        constants.showToast("error", t("FAILED"), error?.message || t("Snap! Something went wrong, Try again"));
      } else {
        constants.showToast("success", t("SUCCESS"), t("Crypto wallets created successfully!"));
        handleModalClose();
        window.location.reload();
      }
    });
  }, [postData, endpoints, handleModalClose, t]);

  const handleReceiveClick = useCallback((wallet) => {
    const walletAddress = wallet?.address;
    const walletNetwork = wallet?.network || wallet?.currency;
    
    if (!walletAddress || !walletNetwork) {
      constants.showToast("error", t("FAILED"), t("Invalid wallet address or network"));
      return;
    }
    setSelectedReceiveWallet(wallet);
    getCryptoReceiveDetails(walletNetwork, walletAddress);
  }, [t]);

  const getCryptoReceiveDetails = useCallback((network, address) => {
    if (!network || !address) {
      constants.showToast("error", t("FAILED"), t("Invalid network or address"));
      return;
    }
    setLoadingReceiveDetails(true);
    setReceiveModalOpen(true);
    endpoints.getCryptoReceiveDetails(network, address, (error, data) => {
      setLoadingReceiveDetails(false);
      if (error) {
        constants.showToast("error", t("FAILED"), error?.message || t("Snap! Something went wrong, Try again"));
        setReceiveModalOpen(false);
      } else {
        setReceiveDetails(data);
      }
    });
  }, [endpoints, t]);

  const columns = useMemo(() => [
    columnHelper.accessor("label", {
      id: "label",
      header: () => <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400">{t("LABEL")}</Text>,
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700">{info.getValue() ?? t("N/A")}</Text>,
    }),
    columnHelper.accessor("address", {
      id: "address",
      header: () => <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400">{t("ADDRESS")}</Text>,
      cell: (info) => (
        <Flex align="center" gap={2} maxWidth="150px">
          <Text color={textColor} fontSize="sm" fontWeight="700" isTruncated maxWidth="120px" title={info.getValue()}>{info.getValue() ?? t("N/A")}</Text>
          <IconButton icon={<CopyIcon />} size="sm" variant="ghost" colorScheme="brand" minWidth="32px"
            onClick={(e) => { e.stopPropagation(); copyToClipboard(info.getValue()); }} aria-label={t("Copy address")} />
        </Flex>
      ),
    }),
    columnHelper.accessor("type", {
      id: "type",
      header: () => <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400">{t("TYPE")}</Text>,
      cell: (info) => {
        const typeValue = info.getValue() ?? t("N/A");
        const typeStyle = getTypeColor(typeValue);
        return (
          <Badge bg={typeStyle.bg} color={typeStyle.color} px={2} py={1} borderRadius="full" fontSize="10px"
            fontWeight="700" whiteSpace="nowrap" textTransform="lowercase" minWidth="fit-content">
            {typeStyle.label}
          </Badge>
        );
      },
    }),
    columnHelper.accessor("currency", {
      id: "currency",
      header: () => <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400">{t("CURRENCY")}</Text>,
      cell: (info) => <Text color={textColor} fontSize="sm" fontWeight="700">{info.getValue() ?? t("N/A")}</Text>,
    }),
    columnHelper.accessor((row) => row.balance?.available_balance, {
      id: "available_balance",
      header: () => <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400">{t("AVAILABLE BALANCE")}</Text>,
      cell: (info) => {
        const balance = info.row.original.balance;
        const walletAddress = info.row.original.address;
        const walletType = info.row.original.type;
        const walletNetwork = info.row.original.network;
        const symbol = balance?.symbol ?? info.row.original.currency ?? "";
        const isNullBalance = balance === null || balance === undefined;
        const isMainWallet = walletType === "main_wallet";
        if (isNullBalance) return renderBalance(null, walletNetwork, symbol, true, walletAddress, true, false, isMainWallet);
        const isRevealed = revealedBalances.has(walletAddress);
        const shouldBlur = !isMainWallet && !isRevealed;
        return renderBalance(balance?.available_balance, walletNetwork, symbol, true, walletAddress, false, shouldBlur, isMainWallet);
      },
    }),
    columnHelper.accessor((row) => row.balance?.pending_received_balance, {
      id: "pending_received_balance",
      header: () => <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400">{t("PENDING BALANCE")}</Text>,
      cell: (info) => {
        const balance = info.row.original.balance;
        const walletAddress = info.row.original.address;
        const walletType = info.row.original.type;
        const walletNetwork = info.row.original.network;
        const symbol = balance?.symbol ?? info.row.original.currency ?? "";
        const isNullBalance = balance === null || balance === undefined;
        const isMainWallet = walletType === "main_wallet";
        if (isNullBalance) return renderBalance(null, walletNetwork, symbol, true, walletAddress, true, false, isMainWallet);
        const isRevealed = revealedBalances.has(walletAddress);
        const shouldBlur = !isMainWallet && !isRevealed;
        return renderBalance(balance?.pending_received_balance, walletNetwork, symbol, true, walletAddress, false, shouldBlur, isMainWallet);
      },
    }),
    columnHelper.accessor("address", {
      id: "receive",
      header: () => <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400">{t("ACTIONS")}</Text>,
      cell: (info) => (
        <Flex gap={2} align="center">
          <Button
            size="xs"
            colorScheme="brand"
            variant="outline"
            leftIcon={<MdQrCode2 />}
            fontSize="10px"
            height="24px"
            isLoading={loadingReceiveDetails && selectedReceiveWallet?.address === info.row.original.address}
            onClick={() => handleReceiveClick(info.row.original)}
          >
            {t("Receive")}
          </Button>
          <Button
            size="xs"
            colorScheme="brand"
            variant="outline"
            leftIcon={<MdHistory />}
            fontSize="10px"
            height="24px"
            onClick={() => navigate(`/admin/crypto-transactions/${info.row.original.currency}/${info.row.original.address}`)}
          >
            {t("Transactions")}
          </Button>
        </Flex>
      ),
    }),
  ], [t, textColor, revealedBalances, loadingBalances, renderBalance, getTypeColor, copyToClipboard, handleReceiveClick, loadingReceiveDetails, selectedReceiveWallet, navigate]);

  const table = useReactTable({
    data: filteredTableData,
    columns,
    state: { sorting, pagination: { pageSize, pageIndex } },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    setPageSize: pageSize,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: true,
  });

  React.useEffect(() => {
    if (filteredTableData.length !== previousWalletCount) {
      table.setPageIndex(0);
      setPreviousWalletCount(filteredTableData.length);
    }
  }, [filteredTableData.length, previousWalletCount, table]);

  const getNextPage = useCallback(() => {
    table.nextPage();
    setTimeout(() => index(table.getState().pagination.pageIndex + 1), 0);
  }, [table, index]);

  const getPreviousPage = useCallback(() => {
    table.previousPage();
    setTimeout(() => index(table.getState().pagination.pageIndex + 1), 0);
  }, [table, index]);

  return (
    <Card flexDirection="column" w="100%" px="0px" overflow="hidden">
      <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
        <Text color={textColor} fontSize="22px" mb="4px" fontWeight="700" lineHeight="100%">
          {t("Addresses")}
        </Text>
        <Button colorScheme="brand" size="sm" leftIcon={<AddIcon />} onClick={onModalOpen}>
          {t("Create Address")}
        </Button>
      </Flex>

      <Box overflowX="auto">
        {tableData.length === 0 ? (
          <Flex justifyContent="center" alignItems="center" height="200px">
            <Text color={textColor} fontSize="sm" fontWeight="500">
              {t("No results found")}! {t("Check")} <NavLink style={{ color: "green" }} to={constants.urls.documentation}>{t("API documentation")}</NavLink> {t("for more details")}
            </Text>
          </Flex>
        ) : (
          <Table variant="simple" color="gray.500" mb="24px" mt="12px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id} colSpan={header.colSpan} pe="10px" borderColor={borderColor} cursor="pointer"
                      onClick={header.column.getToggleSortingHandler()}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id} fontSize={{ sm: "14px" }} minW={{ sm: "150px", md: "200px", lg: "auto" }} borderColor="transparent">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      <Flex justifyContent="space-between" alignItems="center" px="25px" py="10px">
        <Button onClick={getPreviousPage} isDisabled={!table.getCanPreviousPage()}>
          {t("Previous")}
        </Button>
        <Text>
          {t("Page")} {table.getState().pagination.pageIndex + 1} {t("of")} {table.getPageCount()}
        </Text>
        <Button onClick={getNextPage} isDisabled={!table.getCanNextPage()}>
          {t("Next")}
        </Button>
        <Select value={pageSize} width="100px" onChange={(e) => setPagination((prev) => ({ ...prev, pageSize: Number(e.target.value) }))}>
          {sortArray.map((size) => (<option key={size} value={size}>{t("Show")} {size} {t("Max")}</option>))}
        </Select>
      </Flex>

      <CryptoReceiveModal
        isOpen={receiveModalOpen}
        onClose={() => setReceiveModalOpen(false)}
        receiveDetails={receiveDetails}
        loading={loadingReceiveDetails}
        walletAddress={selectedReceiveWallet?.address}
        network={selectedReceiveWallet?.network}
      />

      <Modal isOpen={isModalOpen} onClose={handleModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Create Address")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <select required onChange={(e) => {
              const transfer_networks = networks?.find((item) => item.currency_short === e.target.value)?.transfer_network || null;
              setPostData((prev) => ({ ...prev, type: "sub_wallet", network: e.target.value, transfer_networks: transfer_networks }))
            }}
              style={{ width: "100%", padding: "10px", marginBottom: "24px", borderRadius: "15px", borderColor: "#E2E8F0", borderWidth: "1px", fontSize: "sm", fontWeight: "500" }}>
              <option value="" disabled selected>{t("Select Crypto Network")}</option>
              {networks?.map((item, i) => (
                <option key={i} value={item.currency_short}>{item.currency_short} - {item.currency_symbol}</option>
              ))}
            </select>

            {
              postData?.network && postData?.transfer_networks?.length > 0 && (
                <select
                  required
                  onChange={(e) => {
                    setPostData((prev) => ({ ...prev, transfer_network: e.target.value }))
                  }}
                  style={{
                    width: "100%",
                    padding: "10px",
                    marginBottom: "24px",
                    borderRadius: "15px",
                    borderColor: "#E2E8F0",
                    borderWidth: "1px",
                    fontSize: "sm",
                    fontWeight: "500",
                  }}
                >
                  <option value="" disabled selected>{t("Select Transfer Network")}</option>
                  {postData?.transfer_networks?.map((item, i) => (
                    <option key={i} value={item.network}>
                      {item.name}
                    </option>
                  ))}
                </select>
              )
            }

            {postData?.network && (
              <Input isRequired variant="auth" fontSize="sm" value={postData?.label} type="text"
                onChange={(e) => setPostData((prev) => ({ ...prev, label: e?.target?.value }))}
                placeholder={t("Label")} mb="10px" fontWeight="500" size="lg" />
            )}

            {error.length > 0 && error.map((err, index) => (
              <Text key={index} mt="10px" ms="4px" color="red" fontWeight="400" fontSize="sm">
                * {err.msg}
              </Text>
            ))}
          </ModalBody>

          <ModalFooter gap="12px">
            <Button variant="ghost" fontWeight="500" onClick={handleModalClose}>
              {t("Close")}
            </Button>
            <Button isLoading={loading} isDisabled={loading} variant="brand" fontWeight="500" onClick={proceed}>
              {t("Proceed")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
}